import styled from '@emotion/styled';
import { Box, Typography } from '@mui/material';
import { convertStylesToStyled } from '@/_utils/utils';
import { buildTitleURL } from '@/_utils/titleUtils';
import Poster from '@/components/_common/Poster';
import LinkButton from '@/components/_common/LinkButton';
import PolarisIcon from '@/components/_common/PolarisIcon';
import { TitleEntity } from '@/redux/slices/slice_types';
import { useMemo } from 'react';
import { useConfigValue } from '@/components/_hooks/useConfigValue';
import { OrderPlacementConfig } from '@/__configs/config_types';

type BulkTitleResultItemProps = {
  title: TitleEntity;
};

const BulkResultTitleCard = ({ title }: BulkTitleResultItemProps) => {
  const viewingTitleHeader = useConfigValue<OrderPlacementConfig['viewingTitleHeader']>(['orderPlacementConfig', 'viewingTitleHeader']);
  const titleUrl = useMemo(() => {
    return buildTitleURL({
      path: [...(title.lineage ?? [])].reverse(),
      version: title.id
    });
  }, [title]);

  const viewingTitleValues = {
    ...title,
    lineage_meta: {},
    entity_type: title?.relationships?.root?.entity_type,
    name: title.lineage_meta?.series_name || title.lineage_meta?.feature_name || title.name,
  }

  return (
    <Wrapper>
      <Poster src={title.thumbnail_link || ''} maxWidth={75} display={{ xs: 'none', lg: 'flex' }} />
      <Box ml={2} width="100%">
        {viewingTitleHeader ? viewingTitleHeader(viewingTitleValues) : <Typography variant="h5">{viewingTitleValues.name}</Typography>}
        <TitleDetailsLink href={titleUrl} color="primary" target="_blank" endIcon={<PolarisIcon icon="external_link" height={17} />}>
          View Title Details
        </TitleDetailsLink>
      </Box>
    </Wrapper>
  );
};

export default BulkResultTitleCard;

const Wrapper = styled.div`
  width: 100%;
  display: flex;
  align-items: center;
  padding: ${({ theme }) => theme.spacing(2)};
  border-radius: ${({ theme }) => theme.shape.borderRadius}px;
  background: ${({ theme }) => (theme.palette.mode === 'dark' ? theme.palette.background.paper : 'rgba(255,255,255,.5)')};
  flex-direction: column;

  ${({ theme }) => convertStylesToStyled(theme.customOverrides?.TitleArea)}

  ${({ theme }) => theme.breakpoints.up('lg')} {
    flex-direction: row;
  }

  & label {
    padding-left: 0;
  }
`;

const TitleDetailsLink = styled(LinkButton)(() => ({
  '&:hover': {
    '& svg': {
      opacity: 1
    }
  },
  '& svg': {
    transition: 'opacity 200ms ease-in-out',
    opacity: 0
  }
}));
